import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { DocumentTypeNames } from '@shared/enums/document-type-names.enum';
import { DocumentTypes } from '@shared/enums/document-types.enum';
import { ManagerQueueDataColumns } from '@shared/enums/manager-queue-data-columns.enum';
import { setAge, setAgeColor, toSearchString } from '@shared/helpers/utils';
import { enumToArray, deepCopy } from '@shared/helpers/utils';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IManagerQueueData } from '../../interfaces/manager-queue-data.interface';
import { QueueService } from '../../services/queue.service';
import { throwError } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { QueueStatus } from '@shared/enums/queue-status.enum';
import { QueueItemsMonitoringService } from '@core/signalR/interface/queue-items-monitoring.service';
import { UsersService } from '@shared/services/users.service';

@Component({
  selector: 'app-manager-queue-data',
  templateUrl: './data.component.html',
  styleUrls: ['../../queue.component.scss', './data.component.scss'],
  providers: [QueueItemsMonitoringService]
})
export class ManagerQueueDataComponent implements OnInit {
  QueueStatus = QueueStatus;

  setAge = setAge;
  setAgeColor = setAgeColor;
  columnsToDisplay = enumToArray(ManagerQueueDataColumns);
  documentTypes = enumToArray(DocumentTypes);
  data: IManagerQueueData[] = [];
  DocumentTypeNames = DocumentTypeNames;
  dataSource;
  isLoaded = false;
  maxHoursCount;
  minutesInAgeType;
  isFiltersNotUsed = true;
  userRole: string;
  previousValue = null;
  queueId: number;

  settings = {
    itemPerPage: 20,
    paginationPageCount: null,
    activePage: 0,
    ageType: 'hours',
    filter: {
      show: {
        patient: false,
        documentType: false,
        receivedDate: false,
        age: false,
      },
      value: {
        patient: null,
        documentType: {
          enrollmentForm: false,
          papForm: false,
          prescription: false,
          insuranceCard: false,
          proofOfIncome: false,
          other: false,
        },
        receivedDate: {start: '', end: ''},
        age: [0, 1],
      },
      initialValue: {
        patient: null,
        documentType: {
          enrollmentForm: false,
          papForm: false,
          prescription: false,
          insuranceCard: false,
          proofOfIncome: false,
          other: false,
        },
        receivedDate: {start: '', end: ''},
        age: [0, 1],
      },
    },
  };

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('ageSlider') ageSlider;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvents(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      let shownFilter;
      for (const [key, value] of Object.entries(this.settings.filter.show)) {
        if (this.settings.filter.show[key]) {
          shownFilter = key;
        }
      }
      if (shownFilter) {
        this.applyFilter(shownFilter);
      }
    }
  }

  constructor(
    public queueService: QueueService,
    private router: Router,
    private store: Store<any>,
    private ref: ChangeDetectorRef,
    public usersService: UsersService,
    private queueItemsMonitoringService: QueueItemsMonitoringService,
    private route: ActivatedRoute
  ) {
    this.store.select('user').subscribe((state) => {
      this.userRole = state.role;
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.queueId = parseInt(params.id);
      this.getAllItemsInQueue();
      this.initListeners();
    });
  }

  private initListeners() {
    this.queueItemsMonitoringService.onLockQueueItem(this.queueId).subscribe(
      res => {
        this.updateItemStatus(res, QueueStatus.locked);
      },
      err => {
        console.error(err);
      }
    );

    this.queueItemsMonitoringService.onUnlockQueueItem(this.queueId).subscribe(
      res => {
        this.updateItemStatus(res, QueueStatus.ready);
      },
      err => {
        console.error(err);
      }
    );

    this.queueItemsMonitoringService.onQueueItemCompleted(this.queueId).subscribe(
      res => {
        const index = this.data.findIndex(e => e.id === res.itemId);

        if(index > 0) {
          this.data.splice(index, 1);

          this.dataSource = new MatTableDataSource(this.data);
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          this.setPagination();

          this.ref.detectChanges();
        }
      },
      err => {
        console.error(err);
      }
    );
  }

  private updateItemStatus(data, status) {
    if(!data.itemId || !this.data) return;

    const lockedItem = this.data.find(e => e.id === data.itemId);

    if(!lockedItem) return;

    lockedItem.queueStatus = status;

    this.loadUserImage(lockedItem, data.userId); // Load image async

    this.ref.detectChanges();
  }

  private loadUserImage(item, userId) {
    item.userImageS3Url = undefined; // Mark as no photo while image is loading

    if(!userId) return;

    this.usersService.getPhotoById(userId).subscribe(
      async photo => {
        item.userImageS3Url = await photo;

        this.ref.detectChanges();
      }
    )
  }

  setActivePage(index): void {
    this.settings.activePage = index;
    this.paginator.pageIndex = index;
    this.paginator._changePageSize(this.paginator.pageSize);
  }

  onChangeRange(rangeSlider): void {
    const rangeType = rangeSlider.replace('Slider', '');
    this[rangeSlider].slider.set(this.settings.filter.value[rangeType]);
  }

  openFilter(filter): void {
    this.previousValue = deepCopy(this.settings.filter.value);
    this.closeAllFilters();
    this.settings.filter.show[filter] = true;
  }

  closeFilter(filter): void {
    this.settings.filter.show[filter] = false;
    this.settings.filter.value = this.previousValue;
  }

  closeAllFilters(): void {
    const shownFilters = this.settings.filter.show;
    Object.keys(shownFilters).forEach((k) => {
      shownFilters[k] = false;
    });
    this.settings.filter.value = deepCopy(this.previousValue);
  }

  clearFilter(filter): void {
    switch (filter) {
      case 'documentType':
        this.settings.filter.value[filter] = {
          enrollmentForm: false,
          papForm: false,
          prescription: false,
          insuranceCard: false,
          proofOfIncome: false,
          other: false,
        };
        break;
      case 'receivedDate':
        this.settings.filter.value[filter] = {start: '', end: ''};
        break;
      default:
        this.settings.filter.value[filter] = this.settings.filter.initialValue[filter];
    }

    this.applyFilter(filter);
  }

  clearAllFilters(): void {
    this.settings.filter.value = {
      ...this.settings.filter.initialValue,
      documentType: {
        enrollmentForm: false,
        papForm: false,
        prescription: false,
        insuranceCard: false,
        proofOfIncome: false,
        other: false,
      },
      receivedDate: {start: '', end: ''},
    };

    this.applyFilter('');
  }

  checkIsFilterUsed(): void {
    this.isFiltersNotUsed =
      !!this.settings.filter.value.patient === !!this.settings.filter.initialValue.patient &&
      !this.isDocumentTypeSelected() &&
      !this.settings.filter.value.receivedDate.start &&
      !this.settings.filter.value.receivedDate.end &&
      this.settings.filter.value.age[0] === this.settings.filter.initialValue.age[0] &&
      this.settings.filter.value.age[1] === this.settings.filter.initialValue.age[1];
  }

  setPagination(): void {
    this.settings.paginationPageCount = Math.ceil(this.dataSource.filteredData.length / this.settings.itemPerPage);
  }

  applyFilter(filter): void {
    this.dataSource.filterPredicate = this.customFilterPredicate();
    this.dataSource.filter = this.settings.filter.value;
    this.settings.filter.show[filter] = false;
    this.checkIsFilterUsed();
    this.setPagination();
  }

  countAgeDuration(): void {
    this.minutesInAgeType = this.settings.ageType === 'days' ? 60 * 24 : 60;
    const maxAgeValue = Math.max(...this.data.map((a) => Math.floor(Number(a.age) / this.minutesInAgeType)));
    this.maxHoursCount = maxAgeValue;
    this.settings.filter.value.age = [0, maxAgeValue];
    this.settings.filter.initialValue.age = [0, maxAgeValue];
  }

  getAllItemsInQueue(): void {
    const data = {queueId: this.queueId, take: 1000};

    this.queueService.getAllItemsInQueue(data).subscribe(
      (response) => {
        this.data = response.map((queue) => {
          return {
            id: queue.id,
            patient: queue.patient,
            documentTypes: queue.documentTypes.map((item) => DocumentTypes[item]),
            documentTypeNames: queue.documentTypes.map((item) => DocumentTypeNames[DocumentTypes[item]]),
            receivedDate: queue.createDate,
            age: queue.age.totalMinutes,
            queueStatus: queue.queueStatus,
            lockedByUserId: queue.lockedByUserId,
            createDate: queue.createDate,
          };
        });
        this.dataSource = new MatTableDataSource(this.data);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.setPagination();
        this.countAgeDuration();
        this.settings.filter.initialValue = {...this.settings.filter.value};
        this.isLoaded = true;

        // Load images of user profiles at locked items
        this.data.filter(e => e.queueStatus === 1).forEach(e => {
          this.loadUserImage(e, e.lockedByUserId);
        });
      },
      (error) => {
        const userIsNotAuthorized = error?.statusCode === 403;

        if (userIsNotAuthorized) {
          this.router.navigate(['/']);
        } // TODO: Some message to user that they are not authorized?
        else {
          throwError(error);
        }
      }
    );
  }

  isDocumentTypeSelected(): boolean {
    return Object.values(this.settings.filter.value.documentType).includes(true);
  }

  openItem(queueItem): void {
    if (queueItem.queueStatus !== 1) {
      this.router.navigate(['/document-data-entry'], { queryParams: { id: queueItem.id, queueId: this.queueId}});
    }
  }

  customFilterPredicate(): (data, filter) => boolean {
    const myFilterPredicate = (data, filter): boolean => {
      const patientFilter = filter.patient
        ? toSearchString(data.patient).includes(toSearchString(filter.patient))
        : true;

      let documentTypeFilter = false;
      if (Object.values(filter.documentType).includes(true)) {
        data.documentTypes.forEach((element) => {
          documentTypeFilter = documentTypeFilter || filter.documentType[element];
        });
      } else {
        documentTypeFilter = true;
      }

      const fullEndDate = new Date(filter.receivedDate.end);
      let receivedDateFilter;
      if (filter.receivedDate.start && filter.receivedDate.end) {
        receivedDateFilter =
          new Date(data.receivedDate) >= new Date(filter.receivedDate.start) &&
          new Date(data.receivedDate) <= new Date(fullEndDate.setHours(fullEndDate.getHours() + 24));
      } else if (filter.receivedDate.start && !filter.receivedDate.end) {
        receivedDateFilter = new Date(data.receivedDate) >= new Date(filter.receivedDate.start);
      } else if (!filter.receivedDate.start && filter.receivedDate.end) {
        receivedDateFilter =
          new Date(data.receivedDate) <= new Date(fullEndDate.setHours(fullEndDate.getHours() + 24));
      } else {
        receivedDateFilter = true;
      }

      const ageFilter = filter.age
        ? data.age / this.minutesInAgeType >= filter.age[0] &&
        data.age / this.minutesInAgeType <= filter.age[1] + 1
        : true;
      return patientFilter && documentTypeFilter && receivedDateFilter && ageFilter;
    };

    return myFilterPredicate;
  }
}
